import type { ReactElement, ReactNode } from "react";
import { Suspense } from "react";
import { useMatches } from "react-router-dom";

import { pageSkeletonFromRoute } from "@/components/skeleton-page/PageSkeleton";
import type { AppRouteHandle, RouteSkeletonConfig } from "@/types/routeSkeleton";

function resolveRouteSkeletonConfig(
  matches: ReturnType<typeof useMatches>,
): RouteSkeletonConfig | undefined {
  const handle = matches.at(-1)?.handle as AppRouteHandle | undefined;
  return handle?.skeleton;
}

function wrapSuspenseFallback(
  inner: ReactElement,
  layout: "embedded" | "fullscreen",
): ReactElement {
  if (layout === "fullscreen") {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background p-6"
        role="status"
        aria-live="polite"
      >
        <div className="w-full max-w-lg">{inner}</div>
      </div>
    );
  }

  return (
    <div className="w-full" role="status" aria-live="polite">
      {inner}
    </div>
  );
}

export type PageSuspenseProps = {
  children: ReactNode;
  /**
   * Custom Suspense fallback. When set, `skeleton` and `handle.skeleton` from the leaf route are ignored.
   */
  fallback?: ReactElement;
  /**
   * When `fallback` is omitted: merged over the leaf route’s `handle.skeleton` (embedded only).
   * For fullscreen layout, use this (or `fallback`) — there is no shell route match.
   */
  skeleton?: RouteSkeletonConfig;
  /**
   * Choose fallback chrome: inside {@link AppShell} main column vs full viewport (standalone lazy routes).
   */
  layout?: "embedded" | "fullscreen";
};

/**
 * Suspense boundary with optional custom fallback, otherwise {@link PageSkeleton} from
 * `skeleton` + leaf `handle.skeleton` (embedded) or {@link pageSkeletonFromRoute}.
 */
export function PageSuspense({
  children,
  fallback,
  skeleton,
  layout = "embedded",
}: PageSuspenseProps): ReactElement {
  const matches = useMatches();
  const leaf = layout === "embedded" ? resolveRouteSkeletonConfig(matches) : undefined;
  const merged: RouteSkeletonConfig = { ...leaf, ...skeleton };

  const defaultFallback = wrapSuspenseFallback(pageSkeletonFromRoute(merged), layout);

  return <Suspense fallback={fallback ?? defaultFallback}>{children}</Suspense>;
}

/** Explicit skeleton from route config (no Suspense); same chrome as {@link PageSuspense} default fallback. */
export function LazyRouteSkeleton({
  skeleton,
  layout = "embedded",
}: {
  skeleton?: RouteSkeletonConfig;
  layout?: "embedded" | "fullscreen";
}): ReactElement {
  return wrapSuspenseFallback(pageSkeletonFromRoute(skeleton), layout);
}

export type { PageSkeletonProps } from "@/components/skeleton-page/PageSkeleton";
